/**
 * SDK/CLI Types
 */

// CLI Session status
export type CLISessionStatus = 'pending' | 'authorized' | 'denied' | 'expired';

// CLI Session (Device Auth Flow)
export interface CLISession {
  id: string;
  device_code: string;
  user_code: string;
  status: CLISessionStatus;
  user_id: string | null;
  organization_id: string | null;
  scopes: string[];
  device_name: string | null;
  ip_address: string | null;
  created_at: string;
  expires_at: string;
  authorized_at: string | null;
}

// SDK API Key
export interface SDKApiKey {
  id: string;
  user_id: string;
  organization_id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  allowed_ips: string[] | null;
  allowed_agents: string[] | null;
  rate_limit_per_minute: number;
  last_used_at: string | null;
  total_requests: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CreateSDKKeyInput {
  name: string;
  scopes: string[];
  allowed_ips?: string[];
  allowed_agents?: string[];
  rate_limit_per_minute?: number;
  expires_in_days?: number;
}

// SDK Key Usage
export interface SDKKeyUsage {
  id: string;
  key_id: string;
  endpoint: string;
  method: string;
  status_code: number;
  ip_address: string | null;
  user_agent: string | null;
  latency_ms: number;
  created_at: string;
}

// Usage Stats
export interface SDKKeyUsageStats {
  total_requests: number;
  requests_today: number;
  requests_this_week: number;
  requests_this_month: number;
  avg_latency_ms: number;
  error_rate: number;
  top_endpoints: Array<{
    endpoint: string;
    count: number;
  }>;
  requests_by_day: Array<{
    date: string;
    count: number;
  }>;
}

// Available Scopes
export interface SDKScope {
  id: string;
  name: string;
  description: string;
  category: string;
}

export const SDK_SCOPES: SDKScope[] = [
  // Agent scopes
  { id: 'agent:read', name: 'Read Agents', description: 'List and view agent configurations', category: 'Agents' },
  { id: 'agent:write', name: 'Write Agents', description: 'Create, update, and delete agents', category: 'Agents' },
  { id: 'agent:execute', name: 'Execute Agents', description: 'Execute agent workflows', category: 'Agents' },

  // Execution scopes
  { id: 'execution:read', name: 'Read Executions', description: 'View execution history and logs', category: 'Executions' },
  { id: 'execution:write', name: 'Write Executions', description: 'Cancel or modify executions', category: 'Executions' },

  // Connection scopes
  { id: 'connection:read', name: 'Read Connections', description: 'List available connections', category: 'Connections' },
  { id: 'connection:write', name: 'Write Connections', description: 'Create and manage connections', category: 'Connections' },

  // Environment scopes
  { id: 'environment:read', name: 'Read Environments', description: 'View environment configurations', category: 'Environments' },
  { id: 'environment:write', name: 'Write Environments', description: 'Manage environments', category: 'Environments' },
  { id: 'environment:deploy', name: 'Deploy', description: 'Deploy agents to environments', category: 'Environments' },

  // Admin scopes
  { id: 'admin:read', name: 'Admin Read', description: 'View admin settings', category: 'Admin' },
  { id: 'admin:write', name: 'Admin Write', description: 'Modify admin settings', category: 'Admin' },
];

// API Response types
export interface CLISessionResponse {
  success: boolean;
  data: CLISession;
}

export interface SDKKeyResponse {
  success: boolean;
  data: SDKApiKey;
}

export interface SDKKeyCreatedResponse {
  success: boolean;
  data: {
    key: SDKApiKey;
    full_key: string;
  };
}

export interface SDKKeyListResponse {
  success: boolean;
  data: SDKApiKey[];
}

export interface SDKKeyUsageResponse {
  success: boolean;
  data: SDKKeyUsageStats;
}

// Device auth flow
export interface DeviceCodeResponse {
  success: boolean;
  data: {
    device_code: string;
    user_code: string;
    verification_uri: string;
    expires_in: number;
    interval: number;
  };
}

export interface DeviceTokenResponse {
  success: boolean;
  data: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
  };
  error?: string;
}
